import { Actions, Async, F1 } from '@client/new/helpers/types';
import { VideosModel, VideoType } from './videos.domain';
import { UseVideosParams, useVideos, useVideosUpload } from './videos.hooks';
import { useState } from 'react';
import { useEditVideoMutation } from 'generated/graphql';

export type EditParams = {
  removeAudio?: boolean;
  startTime?: number;
  endTime?: number;
  thumbnailTimestamp?: number;
};

export type VideosModelActionsMap = {
  storeMedia: { videoType: VideoType; file: File };
  cancelEdit: never;
  refetch: never;
  setVideoParams: { file: File; editParams?: EditParams };
  setThumbnail: number;
};

export type VideosModelActions = Actions<VideosModelActionsMap>;
export type VideosModelDispatcher = F1<VideosModelActions, null>;

export const useVideosModel: (
  p: UseVideosParams,
) => Async<VideosModel & { dispatch: VideosModelDispatcher }> = (p) => {
  const asyncVideos = useVideos(p);
  const { uploadFile, uploadMap, clearUploadMapByFileId } = useVideosUpload(p);
  const [media, setMedia] = useState<File | null>(null);
  const [editParams, setEditParams] = useState<EditParams>({});
  const [editVideo] = useEditVideoMutation();

  if (asyncVideos.data.type !== 'fetched') return asyncVideos.data;

  const dispatch: VideosModelDispatcher = (action) => {
    switch (action.type) {
      case 'setVideoParams': {
        setMedia(action.payload.file);
        action.payload.editParams && setEditParams(action.payload.editParams);
        return null;
      }
      case 'setThumbnail': {
        setEditParams({ ...editParams, thumbnailTimestamp: action.payload });
        return null;
      }
      case 'storeMedia': {
        setMedia(action.payload.file);
        return null;
      }
      case 'cancelEdit': {
        setMedia(null);
        setEditParams({});
        return null;
      }
      case 'refetch':
        asyncVideos.refetch();
        return null;
    }
  };

  return {
    type: 'fetched',
    value: {
      videos: asyncVideos.data.value,
      refetchStatus: asyncVideos.refetchStatus,
      uploadStateMap: uploadMap,
      dispatch,
      media,
      editVideo,
    },
  };
};
